import React from 'react';
import { Trash2 } from 'lucide-react';
import type { WorkspaceMember, WorkspaceRole } from '../../../types';

interface Props {
  member: WorkspaceMember;
  isSelf: boolean;
  currentUserIsAdmin: boolean;
  activeAdminCount: number;
  busy: boolean;
  onChangeRole: (userId: string, role: WorkspaceRole) => void;
  onRemove: (member: WorkspaceMember) => void;
}

function displayName(m: WorkspaceMember): string {
  const parts = [m.first_name, m.last_name].filter(Boolean).join(' ').trim();
  return parts || m.email;
}

const RolePill: React.FC<{ role: WorkspaceRole }> = ({ role }) => {
  const cls =
    role === 'admin'
      ? 'bg-purple-500/10 text-purple-300 border-purple-500/30'
      : 'bg-slate-500/10 text-slate-300 border-slate-500/30';
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full border ${cls}`}>
      {role === 'admin' ? 'Admin' : 'Member'}
    </span>
  );
};

const MemberRow: React.FC<Props> = ({
  member,
  isSelf,
  currentUserIsAdmin,
  activeAdminCount,
  busy,
  onChangeRole,
  onRemove,
}) => {
  const soleAdmin =
    member.role === 'admin' && activeAdminCount <= 1 && member.status !== 'disabled';
  const disabledMember = member.status === 'disabled';

  const canEditRole = currentUserIsAdmin && !disabledMember && !soleAdmin;
  const canRemove = currentUserIsAdmin && !isSelf && !disabledMember && !soleAdmin;

  return (
    <tr className={`hover:bg-white/5 transition-colors ${disabledMember ? 'opacity-60' : ''}`}>
      <td className="px-3 py-2.5 text-sm text-white">
        <div className="truncate">
          {displayName(member)}
          {isSelf && <span className="ml-2 text-xs text-cyan-400">(you)</span>}
          {disabledMember && <span className="ml-2 text-xs text-red-400">(removed)</span>}
        </div>
        <div className="text-xs text-gray-400 truncate">{member.email}</div>
      </td>
      <td className="px-3 py-2.5 text-sm text-gray-300 hidden md:table-cell truncate">
        {member.phone || '—'}
      </td>
      <td className="px-3 py-2.5 text-sm">
        {canEditRole ? (
          <select
            value={member.role}
            onChange={(e) => onChangeRole(member.id, e.target.value as WorkspaceRole)}
            disabled={busy}
            className="bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-purple-500"
          >
            <option value="admin" className="bg-slate-800">
              Admin
            </option>
            <option value="member" className="bg-slate-800">
              Member
            </option>
          </select>
        ) : (
          <div className="flex items-center gap-2">
            <RolePill role={member.role} />
            {soleAdmin && !disabledMember && (
              <span className="text-[10px] text-gray-400" title="Promote another member to admin to change this.">
                (sole admin)
              </span>
            )}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 text-right">
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(member)}
            disabled={busy}
            className="p-1.5 text-red-300 hover:text-red-200 hover:bg-red-500/10 rounded disabled:opacity-50"
            aria-label={`Remove ${displayName(member)}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </td>
    </tr>
  );
};

export default MemberRow;
